import { validateEmail } from '@/main/lib/services/ZeroBounce/validateEmail';
import type { NextApiRequest, NextApiResponse } from 'next';

const PIANO_AID = process.env.PIANO_AID || '';
const PIANO_ID_API_BASE_URL = process.env.PIANO_ID_API_BASE_URL || '';
const PIANO_API_TOKEN = process.env.PIANO_API_TOKEN || '';

type PianoError = {
  error_code_list: {
    message: string;
    code?: number;
    field_name?: string;
  }[];
};

type CreateAccountApiSuccess = {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  registration: true;
  email_confirmation_required: boolean;
  pre_confirmed_user: boolean;
  extend_expired_access_enabled: boolean;
};

type CreateAccountApiResponse = CreateAccountApiSuccess | PianoError;

const isCreateAccountApiSuccess = (response: CreateAccountApiResponse): response is CreateAccountApiSuccess => {
  return (response as CreateAccountApiSuccess).access_token !== undefined;
};

interface RegisterSuccessResponse extends CreateAccountApiSuccess {
  success: true;
  status?: string;
  subStatus?: string;
}

interface RegisterFailureResponse {
  success: false;
  message: string;
  isZeroBounceError: boolean;
}

export const isRegisterFailureResponse = (response: RegisterResponse): response is RegisterFailureResponse => {
  return (response as RegisterFailureResponse).success === false;
};

export type RegisterResponse = RegisterSuccessResponse | RegisterFailureResponse;

export const registerPasswordlessPiano = async (
  email: string,
  registrationConsent: boolean,
): Promise<RegisterResponse> => {
  const encodedConsentField = encodeURIComponent(
    JSON.stringify({
      registrationConsent: String(registrationConsent),
    }),
  );
  const registerUserPianoUrl = `${PIANO_ID_API_BASE_URL}/publisher/register?aid=${PIANO_AID}&api_token=${PIANO_API_TOKEN}&email=${encodeURIComponent(email)}${registrationConsent ? `&custom_fields=${encodedConsentField}` : ''}&is_passwordless=true&password=`;

  try {
    const registerUserResponse = await fetch(registerUserPianoUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const registerData: CreateAccountApiResponse = await registerUserResponse.json();

    if (!isCreateAccountApiSuccess(registerData)) {
      const errorCode = registerData.error_code_list[0]?.code;
      const errorMessage = registerData.error_code_list[0]?.message || 'An unknown error occurred during registration.';
      throw new Error(`Request failed with error ${errorCode ? `code ${errorCode}: ` : ': '}${errorMessage}`);
    }

    return { success: true, ...registerData };
  } catch (error) {
    return {
      success: false,
      message: (error as Error).message || 'An unexpected error occurred. Please try again later.',
      isZeroBounceError: false,
    };
  }
};

export default async function handler(req: NextApiRequest, res: NextApiResponse<RegisterResponse>) {
  try {
    const { email, consent } = req.body;

    if (!email) {
      res.status(400).json({ success: false, isZeroBounceError: false, message: 'Email is required.' });
      return;
    }

    const zerobounceResult = await validateEmail(email);

    if (zerobounceResult.success === false) {
      if (zerobounceResult.isServiceError) {
        res.status(502).json({ success: false, isZeroBounceError: true, message: zerobounceResult.message });
        return;
      }
      res.status(400).json({ success: false, isZeroBounceError: true, message: zerobounceResult.message });
      return;
    }

    const result = await registerPasswordlessPiano(email as string, Boolean(consent));
    if (result.success === true) {
      res.status(200).json({ ...result, status: zerobounceResult.status, subStatus: zerobounceResult.subStatus });
      return;
    } else {
      res.status(400).json({ ...result, isZeroBounceError: false });
      return;
    }
  } catch {
    res.status(500).json({ success: false, isZeroBounceError: false, message: 'Internal Server Error' });
    return;
  }
}
